import React from 'react'
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
	useLocation,
} from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { SyncProvider } from './context/SyncContext'
import { RateConfigProvider } from './context/RateConfigContext'
import { AllowedEmailsProvider } from './context/AllowedEmailsContext'
import { UserSessionProvider } from './context/UserSessionContext'
import { getDefaultRouteForRole } from './config/roles'
import ProtectedRoute from './components/auth/ProtectedRoute'
import AuthCallback from './components/auth/AuthCallback'
import { V2Routes } from './V2Routes'
import './styles/index.css'

function PreserveSearchNavigate({ to }: { to: string }) {
	const { search } = useLocation()
	return <Navigate to={`${to}${search}`} replace />
}

function LegacyProjectAnalysisRedirect() {
	const loc = useLocation()
	const suffix = loc.pathname.replace(/^\/project-analysis\/?/, '')
	const target = suffix
		? `/v2/project-analysis/${suffix}`
		: '/v2/project-analysis'
	return <Navigate to={`${target}${loc.search}`} replace />
}

function RootHomeRedirect() {
	const { currentUser } = useAuth()
	return (
		<Navigate to={getDefaultRouteForRole(currentUser?.role)} replace />
	)
}

function CatchAllRedirect() {
	const { currentUser } = useAuth()
	const target = currentUser
		? getDefaultRouteForRole(currentUser.role || 'finance')
		: '/'
	return <Navigate to={target} replace />
}

function V2Shell() {
	return (
		<ProtectedRoute>
			<UserSessionProvider>
				<V2Routes />
			</UserSessionProvider>
		</ProtectedRoute>
	)
}

function App() {
	return (
		<Router>
			<AuthProvider>
				<RateConfigProvider>
					<AllowedEmailsProvider>
						<SyncProvider>
							<Routes>
								<Route path="/auth/login" element={<AuthCallback />} />
								{/* Subscription management — removed, will be rewritten separately */}
								<Route
									path="/"
									element={(
										<ProtectedRoute>
											<RootHomeRedirect />
										</ProtectedRoute>
									)}
								/>
								<Route
									path="/my-dashboard"
									element={(
										<ProtectedRoute>
											<PreserveSearchNavigate to="/v2/my-dashboard" />
										</ProtectedRoute>
									)}
								/>
								<Route
									path="/timesheet-analysis"
									element={(
										<ProtectedRoute>
											<PreserveSearchNavigate to="/v2/employee-analysis" />
										</ProtectedRoute>
									)}
								/>
								<Route
									path="/project-analysis/*"
									element={(
										<ProtectedRoute>
											<LegacyProjectAnalysisRedirect />
										</ProtectedRoute>
									)}
								/>
								<Route
									path="/client-acquisition-retention"
									element={(
										<ProtectedRoute>
											<PreserveSearchNavigate to="/v2/client-acquisition-retention" />
										</ProtectedRoute>
									)}
								/>
								<Route path="/v2" element={<V2Shell />} />
								<Route path="/v2/*" element={<V2Shell />} />
								<Route path="*" element={<CatchAllRedirect />} />
							</Routes>
						</SyncProvider>
					</AllowedEmailsProvider>
				</RateConfigProvider>
			</AuthProvider>
		</Router>
	)
}

export default App
export { V2Routes } from './V2Routes'
